// src/pages/UserListPage.tsx
import BaseTable from "@/components/BaseTable/BaseTable";
import { getTenantId } from "@/lib/storeManager";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/projects/NPS/NpsUser/utils/navigationRoutes";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserStatementofHoldingService from "@/services/NPS/NpsUser/APIs/UserStatementofHoldingService";
import { Scheme } from "@/utils/types/UserStatementofHolding";
import { BarChart3, Download } from "lucide-react";

const InvestmentsTable: React.FC = () => {
  const navigate = useNavigate();
  const tenantId = getTenantId() ?? "";
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const {
    data,
    isLoading,
    refetch: refetchUserStatementofHolding,
  } = useQuery({
    queryKey: ["userStatementofHolding", tenantId, page, pageSize],
    queryFn: () =>
      UserStatementofHoldingService.listUserStatementofHolding(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  // const handleView = (id: string) => {
  //   navigate(navigationRoutes.NPS_USER_DETAILS(id));
  // };

  const columns: ProColumns<Scheme>[] = [
    {
      title: "Scheme",
      render: (_, record) => `${record.schemeName}`,
    },
    { title: "Units", dataIndex: "units" },
    { title: "Free Units", dataIndex: "freeUnits" },
    { title: "Blocked Units", dataIndex: "blckdUnits" },
    { title: "NAV (₹)", dataIndex: "nav" },
    { title: "NAV Date", dataIndex: "navDate" },
    { title: "Value (₹)", dataIndex: "value" },
    // {
    //   title: "Actions",
    //   width: 100,
    //   valueType: "option",
    //   render: (_, record) => [
    //     <CustomButton
    //       key="view"
    //       onClick={() => handleView(record.ackId)}
    //       icon={<Eye size={16} />}
    //       type="text"
    //       title="View Nps User"
    //     />,
    //   ],
    // },
  ];

  //   const toolBarRender = () => {
  //     return [
  //       <CustomButton
  //         key="add"
  //         type="primary"
  //         onClick={handleCreate}
  //         title="Add User"
  //       >
  //         <Plus size={16} className="mr-1" /> {t("Add User")}
  //       </CustomButton>,
  //     ];
  //   };

  return (
    <div className="mb-6 rounded-xl border bg-card shadow-sm">
      {/* HEADER */}
      <div className="border-b p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-secondary">
              <BarChart3 className="h-5 w-5 text-primary" />
              Statement of Holding - Tier 1
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Scheme-wise investment breakdown with NAV and units as on
              10/12/2025
            </p>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs text-green-700">
              Tier Status - Active
            </span>

            <button className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition hover:bg-muted">
              <Download className="h-4 w-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="p-6">
        <BaseTable<Scheme>
          loading={isLoading}
          columns={columns}
          dataSource={(data?.sohData?.tier1?.schemes ?? []) as Scheme[]}
          page={page}
          pageSize={pageSize}
          total={10}
          onPageChange={(page, pageSize) => {
            setPage(page);
            setPageSize(pageSize ?? 10);
            searchParams.set("page", page.toString());
            searchParams.set("pageSize", pageSize.toString());
            navigate(
              `${navigationRoutes.NPS_USER_LIST}?${searchParams.toString()}`
            );
            refetchUserStatementofHolding();
          }}
          rowKey="id"
          headerTitle=""
          toolBarRender={undefined}
          request={() => refetchUserStatementofHolding()}
          tableName={gridName.investmentsTable}
        />
      </div>
    </div>
  );
};

export default InvestmentsTable;
